import React, { useState, useEffect, useMemo } from 'react';
import { Container, Button, Form, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import Icon from '../../UI/Icon';
import { getAllWords, getAllStoryImages } from '../../../services/gameService';
import { getProblemSounds } from '../../../services/pronunciationTestService';
import gameWon from '../../../assets/gameWon.png';
import Confetti from 'react-confetti';
import sprechheldenHeldSeated from '../../../assets/sprechheldenHeld_seated.png';
import background1 from '../../../assets/background_1.png';
import helpLogo from '../../../assets/logo_logo_sh.png';
import { auth } from '../../../firebase';
import { LuGamepad2 } from "react-icons/lu";
import { isPhone as checkIsPhone, isTabletPortrait, setupDeviceDetection } from '../../../utils/deviceDetection';

function StoryGame({ difficulty = 'medium', onBackToGames }) {
  const [selectedImages, setSelectedImages] = useState(() => {
    const numSlots = difficulty === 'easy' ? 3 : difficulty === 'medium' ? 4 : 5;
    return Array(numSlots).fill(null);
  });
  
  const getSlotTypes = () => {
    const availableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;

    if (difficulty === 'easy') {
      const requiredStory = 1;
      const actualStory = Math.min(requiredStory, availableStoryCount);
      const slots = [
        { type: 'words', label: 'Wortbild' },
        { type: 'words', label: 'Wortbild' }
      ];
      for (let i = 0; i < actualStory; i++) {
        slots.push({ type: 'story_images', label: 'Geschichtenbild' });
      }
      for (let i = actualStory; i < requiredStory; i++) {
        slots.push({ type: 'words', label: 'Wortbild' });
      }
      return slots;
    }
    if (difficulty === 'medium') {
      const requiredStory = 2;
      const actualStory = Math.min(requiredStory, availableStoryCount);
      const slots = [
        { type: 'words', label: 'Wortbild' },
        { type: 'words', label: 'Wortbild' }
      ];
      for (let i = 0; i < actualStory; i++) {
        slots.push({ type: 'story_images', label: 'Geschichtenbild' });
      }
      for (let i = actualStory; i < requiredStory; i++) {
        slots.push({ type: 'words', label: 'Wortbild' });
      }
      return slots;
    }
    return Array(selectedImages.length).fill({ type: 'any', label: 'Beliebig' });
  };
  const [showStoryMode, setShowStoryMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedImage, setDraggedImage] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [imageAssets, setImageAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLautModal, setShowLautModal] = useState(true);
  const [selectedLauts, setSelectedLauts] = useState([]);
  const [availableLauts, setAvailableLauts] = useState([]);
  const [allWords, setAllWords] = useState([]);
  const [storyImages, setStoryImages] = useState([]);
  const [usedImages, setUsedImages] = useState([]);
  const [storyCompleted, setStoryCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showHelpText, setShowHelpText] = useState(false);

  // Helper function to show alert modal
  const showAlert = (message) => {
    setAlertMessage(message);
    setShowAlertModal(true);
  };

  // Helper function to show help text
  const handleHelpClick = () => {
    setShowHelpText(true);
    // Hide help text after 5 seconds
    setTimeout(() => {
      setShowHelpText(false);
    }, 5000);
  };

  // Add CSS animations
  const styles = `
    @keyframes float {
      0% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-15px);
      }
      100% {
        transform: translateY(0px);
      }
    }
    @keyframes storyShimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;

  // Inject styles into document head
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('story-game-styles');
    if (!existingStyle) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'story-game-styles';
      styleSheet.innerText = styles;
      document.head.appendChild(styleSheet);
    }
  }

  // Helper function to determine category from word
  const getCategoryForWord = (word) => {
    const categoryMap = {
      'Hund': 'Tiere', 'Katze': 'Tiere', 'Vogel': 'Tiere', 'Fisch': 'Tiere', 'Bär': 'Tiere', 'Löwe': 'Tiere', 'Elefant': 'Tiere', 'Affe': 'Tiere', 'Maus': 'Tiere',
      'Baum': 'Natur', 'Blume': 'Natur', 'Sonne': 'Natur', 'Mond': 'Natur', 'Wolke': 'Natur', 'Regenbogen': 'Natur', 'Wasser': 'Natur', 'Regen': 'Natur',
      'Auto': 'Fahrzeuge', 'Bus': 'Fahrzeuge', 'Fahrrad': 'Fahrzeuge', 'Flugzeug': 'Fahrzeuge', 'Schiff': 'Fahrzeuge', 'Zug': 'Fahrzeuge',
      'Ball': 'Spielzeug', 'Puppe': 'Spielzeug', 'Teddy': 'Spielzeug',
      'Apfel': 'Essen', 'Banane': 'Essen', 'Pizza': 'Essen', 'Eis': 'Essen',
      'Haus': 'Gebäude', 'Schule': 'Gebäude', 'Burg': 'Gebäude',
      'Buch': 'Objekte', 'Schirm': 'Objekte', 'Knochen': 'Objekte',
      'Spielen': 'Aktivitäten', 'Lesen': 'Aktivitäten', 'Strand': 'Aktivitäten',
      'Garten': 'Orte', 'Nest': 'Orte', 'Himmel': 'Orte'
    };
    return categoryMap[word] || 'Allgemein';
  };

  useEffect(() => {
    let cancelled = false;

    const fetchInitialData = async () => {
      try {
        setLoading(true);

        const [words, images] = await Promise.all([
          getAllWords(),
          getAllStoryImages()
        ]);

        if (cancelled) return;

        setAllWords(words);
        setStoryImages(images);

        const lauts = words
          .filter(word => word.laut && Array.isArray(word.laut))
          .flatMap(word => word.laut)
          .map(laut => laut && typeof laut === 'string' ? laut.trim() : laut)
          .filter(laut => laut && typeof laut === 'string' && laut !== '')
          .filter((laut, index, array) => array.indexOf(laut) === index)
          .sort();

        setAvailableLauts(lauts);
        setLoading(false);

        // Pre-select severe problem sounds in the background — does not block the UI.
        // Only applies if the user hasn't already picked lauts manually by the time this resolves.
        const user = auth.currentUser;
        if (!user) return;

        const problemSoundsData = await getProblemSounds(user.uid);
        if (cancelled) return;

        const severeSounds = problemSoundsData.severeProblemSounds;
        if (!severeSounds || !Array.isArray(severeSounds) || severeSounds.length === 0) return;

        const soundsToPreselect = severeSounds
          .filter(sound => lauts.some(laut => laut.toLowerCase() === sound.toLowerCase()))
          .map(sound => lauts.find(laut => laut.toLowerCase() === sound.toLowerCase()))
          .filter(Boolean)
          .slice(0, 2);

        setSelectedLauts(prev => (prev.length === 0 ? soundsToPreselect : prev));
      } catch (error) {
        console.error('Error loading story game data:', error);
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitialData();

    return () => { cancelled = true; };
  }, []);

  // Device detection on component mount using centralized utility
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      // Only block actual phones, allow tablets (including portrait mode)
      setIsPhone(checkIsPhone());
      setIsTabletPortraitMode(isTabletPortrait());
    });

    return cleanup;
  }, []);

  // Filter and convert words based on selected lauts from both collections
  useEffect(() => {
    if (selectedLauts.length === 0) {
      setImageAssets([]);
      return;
    }

    let assets = [];

    // Get images from Words collection based on selected lauts
    const filteredWords = allWords.filter(word => 
      word.word && 
      word.linkedImage && 
      word.laut && 
      Array.isArray(word.laut) &&
      selectedLauts.some(selectedLaut => word.laut.includes(selectedLaut))
    );

    const wordAssets = filteredWords.map(word => ({
      id: word.id,
      name: word.word,
      category: getCategoryForWord(word.word),
      imageUrl: word.linkedImage,
      laut: word.laut,
      source: 'words',
      english: word.english || '',
      description: word.description || ''
    }));

    assets = [...assets, ...wordAssets];

    // Get images from story_images collection based on selected lauts
    const filteredStoryImages = storyImages.filter(image => 
      image.imageUrl && 
      image.laut && 
      Array.isArray(image.laut) &&
      selectedLauts.some(selectedLaut => image.laut.includes(selectedLaut))
    );

    const storyAssets = filteredStoryImages.map(image => {
      // Try to extract a name from filename or use matching laut as name
      const filename = image.filename || '';
      const nameFromFile = filename
        .replace(/\.(png|jpg|jpeg|gif|webp)$/i, '')
        .replace(/_\d+x\d+$/i, '')
        .replace(/^story_\d+_?/, '');
      // Find a laut that matches the selected lauts, otherwise fall back to first laut
      const matchingLaut = image.laut.find(l => selectedLauts.includes(l));
      const displayName = nameFromFile || matchingLaut || image.laut[0] || 'Bild';

      return {
        id: `story_${image.id}`,
        name: displayName,
        category: 'Geschichtenbilder',
        imageUrl: image.imageUrl,
        laut: image.laut,
        source: 'story_images',
        filename: image.filename || '',
        confirmed: image.confirmed || false
      };
    });

    assets = [...assets, ...storyAssets];

    // Remove duplicates based on imageUrl
    const uniqueAssets = assets.filter((asset, index, self) => 
      index === self.findIndex(a => a.imageUrl === asset.imageUrl)
    );

    setImageAssets(uniqueAssets);
  }, [selectedLauts, allWords, storyImages]);

  // Handle laut selection with max 2 validation
  // Warm the browser image cache as soon as the asset set is known, so the
  // grid renders with images already downloaded once the user closes the modal.
  useEffect(() => {
    if (imageAssets.length === 0) return;
    imageAssets.forEach(asset => {
      if (asset.imageUrl) {
        const img = new Image();
        img.src = asset.imageUrl;
      }
    });
  }, [imageAssets]);

  const handleLautSelection = (laut) => {
    setSelectedLauts(prev => {
      if (prev.includes(laut)) {
        // Remove laut if already selected
        return prev.filter(l => l !== laut);
      } else {
        // Add laut if not selected and under limit
        if (prev.length < 2) {
          return [...prev, laut];
        }
        return prev; // Don't add if already at max
      }
    });
  };

  // Start game with selected lauts
  const startGameWithLauts = () => {
    if (selectedLauts.length === 0) {
      showAlert('Bitte wähle mindestens einen Laut aus!');
      return;
    }
    setShowLautModal(false);
  };

  // Reset laut selection
  const resetLautSelection = () => {
    setSelectedLauts([]);
  };

  const toggleImageUsed = (imageId) => {
    setUsedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const resetUsedImages = () => {
    setUsedImages([]);
  };


  const filteredImages = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return imageAssets;
    return imageAssets.filter(img =>
      img.name.toLowerCase().includes(term) ||
      img.category.toLowerCase().includes(term)
    );
  }, [imageAssets, searchTerm]);

  const selectedImageIds = useMemo(
    () => new Set(selectedImages.filter(img => img !== null).map(img => img.id)),
    [selectedImages]
  );

  // Returns true if the URL is already in the browser's memory cache —
  // the underlying image decodes synchronously. Used to float already-loaded
  // thumbnails to the top of the grid so users see content immediately on
  // subsequent renders / return visits.
  const isImageCached = (url) => {
    if (!url) return false;
    const img = new Image();
    img.src = url;
    return img.complete && img.naturalWidth > 0;
  };

  const sortCachedFirst = (images) => {
    const cachedIndex = images.map((img, idx) => ({ img, idx, cached: isImageCached(img.imageUrl) }));
    cachedIndex.sort((a, b) => {
      if (a.cached !== b.cached) return a.cached ? -1 : 1;
      return a.idx - b.idx; // stable within each group
    });
    return cachedIndex.map(entry => entry.img);
  };

  const wordsImages = useMemo(
    () => sortCachedFirst(
      filteredImages.filter(img => img.source === 'words' && !selectedImageIds.has(img.id))
    ),
    [filteredImages, selectedImageIds]
  );

  const storyImagesFiltered = useMemo(
    () => sortCachedFirst(
      filteredImages.filter(img => img.source === 'story_images' && !selectedImageIds.has(img.id))
    ),
    [filteredImages, selectedImageIds]
  );

  if (loading) {
    return (
      <Container fluid>
        <div className="text-center" style={{ marginTop: '50px' }}>
          <h2>Lade Bilder...</h2>
          <p>Bitte warten...</p>
        </div>
      </Container>
    );
  }

  const handleDragStart = (_, image) => {
    setDraggedImage(image);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, slotIndex) => {
    e.preventDefault();
    if (draggedImage) {
      // Check if image is already selected (prevent duplicates in all modes)
      const isAlreadySelected = selectedImages.some(img => img && img.id === draggedImage.id);
      if (isAlreadySelected) {
        showAlert('Dieses Bild ist bereits ausgewählt!');
        setDraggedImage(null);
        return;
      }
      
      // Check slot type restrictions for easy and medium difficulty
      if (difficulty === 'easy' || difficulty === 'medium') {
        const slotTypes = getSlotTypes();
        const slotType = slotTypes[slotIndex].type;
        const imageSource = draggedImage.source;
        
        if (slotType !== 'any' && slotType !== imageSource) {
          setDraggedImage(null);
          return;
        }
      } else {
        // For hard mode, limit each type to max 4 images (must have at least 1 of each, when available)
        const availableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;
        const availableWordCount = imageAssets.filter(img => img.source === 'words').length;
        const maxStoryImages = availableWordCount > 0 ? 4 : 5;
        const maxWordImages = availableStoryCount > 0 ? 4 : 5;

        if (draggedImage.source === 'story_images') {
          const existingImage = selectedImages[slotIndex];
          let currentStoryImages = selectedImages.filter(img => img && img.source === 'story_images').length;

          if (existingImage && existingImage.source !== 'story_images') {
            if (currentStoryImages >= maxStoryImages) {
              showAlert(`Im schweren Modus kannst du maximal ${maxStoryImages} Geschichtenbilder auswählen.`);
              setDraggedImage(null);
              return;
            }
          } else if (!existingImage && currentStoryImages >= maxStoryImages) {
            showAlert(`Im schweren Modus kannst du maximal ${maxStoryImages} Geschichtenbilder auswählen.`);
            setDraggedImage(null);
            return;
          }
        } else if (draggedImage.source === 'words') {
          const existingImage = selectedImages[slotIndex];
          let currentWordImages = selectedImages.filter(img => img && img.source === 'words').length;

          if (existingImage && existingImage.source !== 'words') {
            if (currentWordImages >= maxWordImages) {
              showAlert(`Im schweren Modus kannst du maximal ${maxWordImages} Wortbilder auswählen.`);
              setDraggedImage(null);
              return;
            }
          } else if (!existingImage && currentWordImages >= maxWordImages) {
            showAlert(`Im schweren Modus kannst du maximal ${maxWordImages} Wortbilder auswählen.`);
            setDraggedImage(null);
            return;
          }
        }
      }
      
      const newSelected = [...selectedImages];
      newSelected[slotIndex] = draggedImage;
      setSelectedImages(newSelected);
      setDraggedImage(null);
    }
  };

  const handleImageClick = (image) => {
    // Check if image is already selected (prevent duplicates in all modes)
    const isAlreadySelected = selectedImages.some(img => img && img.id === image.id);
    if (isAlreadySelected) {
      showAlert('Dieses Bild ist bereits ausgewählt!');
      return;
    }
    
    if (difficulty === 'easy' || difficulty === 'medium') {
      // For easy and medium difficulty, find first compatible empty slot
      const slotTypes = getSlotTypes();
      const imageSource = image.source;
      
      for (let i = 0; i < selectedImages.length; i++) {
        if (selectedImages[i] === null) {
          const slotType = slotTypes[i].type;
          if (slotType === 'any' || slotType === imageSource) {
            const newSelected = [...selectedImages];
            newSelected[i] = image;
            setSelectedImages(newSelected);
            return;
          }
        }
      }
      
      // No compatible slot found - silently prevent the action
      return;
    } else {
      // For hard mode, limit each type based on availability of the other type
      const availableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;
      const availableWordCount = imageAssets.filter(img => img.source === 'words').length;
      const maxStoryImages = availableWordCount > 0 ? 4 : 5;
      const maxWordImages = availableStoryCount > 0 ? 4 : 5;

      if (image.source === 'story_images') {
        const currentStoryImages = selectedImages.filter(img => img && img.source === 'story_images').length;
        if (currentStoryImages >= maxStoryImages) {
          showAlert(`Im schweren Modus kannst du maximal ${maxStoryImages} Geschichtenbilder auswählen.`);
          return;
        }
      } else if (image.source === 'words') {
        const currentWordImages = selectedImages.filter(img => img && img.source === 'words').length;
        if (currentWordImages >= maxWordImages) {
          showAlert(`Im schweren Modus kannst du maximal ${maxWordImages} Wortbilder auswählen.`);
          return;
        }
      }

      // Find first empty slot
      const emptySlotIndex = selectedImages.findIndex(img => img === null);
      if (emptySlotIndex !== -1) {
        const newSelected = [...selectedImages];
        newSelected[emptySlotIndex] = image;
        setSelectedImages(newSelected);
      }
    }
  };

  const removeFromSlot = (slotIndex) => {
    const newSelected = [...selectedImages];
    newSelected[slotIndex] = null;
    setSelectedImages(newSelected);
    setHoveredSlot(null); // Clear hover state to prevent red overlay persistence
  };

  const startStory = () => {
    const filledSlots = selectedImages.filter(img => img !== null);
    
    if (difficulty === 'easy') {
      // For easy mode, require all 3 slots to be filled
      if (filledSlots.length < 3) {
        showAlert('Bitte fülle alle 3 Plätze aus!');
        return;
      }

      // Check for duplicates
      const uniqueImages = new Set(filledSlots.map(img => img.id));
      if (uniqueImages.size !== filledSlots.length) {
        showAlert('Jedes Bild darf nur einmal verwendet werden!');
        return;
      }
    } else if (difficulty === 'medium') {
      // For medium mode, require all 4 slots to be filled
      if (filledSlots.length < 4) {
        showAlert('Bitte fülle alle 4 Plätze aus!');
        return;
      }

      // Check for duplicates
      const uniqueImages = new Set(filledSlots.map(img => img.id));
      if (uniqueImages.size !== filledSlots.length) {
        showAlert('Jedes Bild darf nur einmal verwendet werden!');
        return;
      }
    } else {
      // For hard mode
      const minRequired = 5;
      if (filledSlots.length < minRequired) {
        showAlert(`Bitte wähle alle ${minRequired} Plätze aus!`);
        return;
      }
      
      // Check for duplicates in hard mode too
      const uniqueImages = new Set(filledSlots.map(img => img.id));
      if (uniqueImages.size !== filledSlots.length) {
        showAlert('Jedes Bild darf nur einmal verwendet werden!');
        return;
      }
      
      // Check type constraints based on availability
      const availableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;
      const wordImages = filledSlots.filter(img => img.source === 'words');
      const storyImagesSelected = filledSlots.filter(img => img.source === 'story_images');

      if (wordImages.length < 1) {
        showAlert('Im schweren Modus musst du mindestens 1 Wortbild auswählen. Maximal 4 Geschichtenbilder sind erlaubt!');
        return;
      }

      if (availableStoryCount > 0 && storyImagesSelected.length < 1) {
        showAlert('Im schweren Modus musst du mindestens 1 Geschichtenbild auswählen. Maximal 4 Wortbilder sind erlaubt!');
        return;
      }
    }
    
    resetUsedImages();
    setShowStoryMode(true);
  };

  const selectRandomImages = () => {
    if (imageAssets.length === 0) return;
    
    // Get currently selected image IDs to avoid duplicates
    const selectedIds = selectedImages
      .filter(img => img !== null)
      .map(img => img.id);
    
    // Get available images (excluding already selected ones)
    const availableImages = imageAssets.filter(img => !selectedIds.includes(img.id));
    const newSelected = [...selectedImages];
    
    if (difficulty === 'easy' || difficulty === 'medium') {
      // For easy and medium difficulty, respect slot type restrictions
      const slotTypes = getSlotTypes();
      const availableWordImages = availableImages.filter(img => img.source === 'words');
      const availableStoryImages = availableImages.filter(img => img.source === 'story_images');
      
      // Shuffle both types
      const shuffledWordImages = [...availableWordImages].sort(() => Math.random() - 0.5);
      const shuffledStoryImages = [...availableStoryImages].sort(() => Math.random() - 0.5);
      
      let wordImageIndex = 0;
      let storyImageIndex = 0;
      
      for (let i = 0; i < newSelected.length; i++) {
        if (newSelected[i] === null) {
          const slotType = slotTypes[i].type;
          if (slotType === 'words' && wordImageIndex < shuffledWordImages.length) {
            newSelected[i] = shuffledWordImages[wordImageIndex];
            wordImageIndex++;
          } else if (slotType === 'story_images' && storyImageIndex < shuffledStoryImages.length) {
            newSelected[i] = shuffledStoryImages[storyImageIndex];
            storyImageIndex++;
          }
        }
      }
    } else {
      // For hard mode, ensure at least 1 of each type when available (max based on availability)
      const availableWordImages = availableImages.filter(img => img.source === 'words');
      const availableStoryImages = availableImages.filter(img => img.source === 'story_images');
      const totalAvailableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;
      const totalAvailableWordCount = imageAssets.filter(img => img.source === 'words').length;
      const maxStoryImages = totalAvailableWordCount > 0 ? 4 : 5;
      const maxWordImages = totalAvailableStoryCount > 0 ? 4 : 5;

      // Count existing selections
      let currentWordImages = newSelected.filter(img => img && img.source === 'words').length;
      let currentStoryImages = newSelected.filter(img => img && img.source === 'story_images').length;
      let emptySlots = newSelected.filter(img => img === null).length;

      // Shuffle both types
      const shuffledWordImages = [...availableWordImages].sort(() => Math.random() - 0.5);
      const shuffledStoryImages = [...availableStoryImages].sort(() => Math.random() - 0.5);

      // Fill empty slots respecting type limits based on availability
      let wordIndex = 0;
      let storyIndex = 0;

      for (let i = 0; i < newSelected.length; i++) {
        if (newSelected[i] === null) {
          // If we don't have any word images yet and this is the last slot, add a word image
          if (currentWordImages === 0 && emptySlots === 1 && wordIndex < shuffledWordImages.length) {
            newSelected[i] = shuffledWordImages[wordIndex];
            wordIndex++;
            currentWordImages++;
          }
          // If we don't have any story images yet, this is the last slot, and story images exist, add one
          else if (currentStoryImages === 0 && emptySlots === 1 && storyIndex < shuffledStoryImages.length && totalAvailableStoryCount > 0) {
            newSelected[i] = shuffledStoryImages[storyIndex];
            storyIndex++;
            currentStoryImages++;
          }
          // If we already hit max story images, add a word image
          else if (currentStoryImages >= maxStoryImages && wordIndex < shuffledWordImages.length) {
            newSelected[i] = shuffledWordImages[wordIndex];
            wordIndex++;
            currentWordImages++;
          }
          // If we already hit max word images, add a story image
          else if (currentWordImages >= maxWordImages && storyIndex < shuffledStoryImages.length) {
            newSelected[i] = shuffledStoryImages[storyIndex];
            storyIndex++;
            currentStoryImages++;
          }
          // Otherwise, randomly choose between word and story images
          else {
            const canAddStory = currentStoryImages < maxStoryImages && storyIndex < shuffledStoryImages.length;
            const canAddWord = currentWordImages < maxWordImages && wordIndex < shuffledWordImages.length;

            if (canAddStory && canAddWord) {
              if (Math.random() < 0.5) {
                newSelected[i] = shuffledStoryImages[storyIndex];
                storyIndex++;
                currentStoryImages++;
              } else {
                newSelected[i] = shuffledWordImages[wordIndex];
                wordIndex++;
                currentWordImages++;
              }
            } else if (canAddStory) {
              newSelected[i] = shuffledStoryImages[storyIndex];
              storyIndex++;
              currentStoryImages++;
            } else if (canAddWord) {
              newSelected[i] = shuffledWordImages[wordIndex];
              wordIndex++;
              currentWordImages++;
            }
          }
          emptySlots--;
        }
      }
    }
    
    setSelectedImages(newSelected);
  };

  const finishStory = () => {
    setStoryCompleted(true);
    setShowConfetti(true);
    // Stop confetti after 5 seconds
    setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
  };

  const selectNewImages = () => {
    setShowStoryMode(false);
    resetUsedImages();
    setStoryCompleted(false);
    setShowConfetti(false);
    const numSlots = difficulty === 'easy' ? 3 : difficulty === 'medium' ? 4 : 5;
    setSelectedImages(Array(numSlots).fill(null));
  };

  if (showStoryMode) {
    const filledImages = selectedImages.filter(img => img !== null);

    return (
      <div style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container fluid style={{ position: 'relative', minHeight: '100vh', zIndex: 1 }}>
        {storyCompleted ? (
          <>
            {/* Story Complete Section with GameWon Character */}
            <div className="text-center" style={{ marginBottom: '30px', paddingBottom: '0px' }}>
              <h2 style={{ margin: 0, color: 'green' }}>Geschichte abgeschlossen!</h2>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: '400px',
              position: 'relative',
              marginTop: '20px'
            }}>
              {/* GameWon character with relative positioning */}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={gameWon}
                  alt="Game Won Character"
                  style={{
                    width: '300px',
                    height: 'auto',
                    zIndex: 1,
                    animation: 'float 3s ease-in-out infinite',
                    marginTop: isTabletPortraitMode ? '60px' : '0'
                  }}
                />
                
                {/* Speech bubble text positioned relative to character */}
                <div
                  className="game-complete-speech-bubble"
                  style={{
                    position: 'absolute',
                    top: isTabletPortraitMode ? '-40px' : '-20px',
                    left: isTabletPortraitMode ? '430px' : '350px',
                    transform: 'translateX(-50%)',
                    zIndex: 2,
                    backgroundColor: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '20px',
                    border: '2px solid #333333',
                    fontSize: '18px',
                    color: '#333',
                    textAlign: 'center',
                    minWidth: '280px',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: '600'
                  }}
                >
                  <span>
                    Fantastisch! Du hast eine tolle Geschichte erzählt!
                  </span>
                </div>
              </div>
            </div>

            {/* Buttons section for completed story */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '40px' }}>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="new-story-tooltip">Neue Geschichte</Tooltip>}
              >
                <Button 
                  variant="success"
                  size="lg"
                  onClick={selectNewImages}
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <Icon name="refresh" size="lg" />
                </Button>
              </OverlayTrigger>
              {onBackToGames && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
                >
                  <Button 
                    variant="secondary"
                    size="lg"
                    onClick={onBackToGames}
                    style={{
                      minWidth: '60px',
                      minHeight: '60px',
                      borderRadius: '50%',
                      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    <LuGamepad2 size={24} />
                  </Button>
                </OverlayTrigger>
              )}
            </div>

            {/* Confetti Effect */}
            {storyCompleted && (
              <Confetti
                width={window.innerWidth}
                height={window.innerHeight}
                recycle={showConfetti}
                numberOfPieces={200}
                gravity={0.3}
                style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
              />
            )}
          </>
        ) : (
          <>
            
            <div className="text-center" style={{ marginBottom: '30px', paddingBottom: '0px' }}>
              <h2 style={{ margin: 0 }}>Geschichte erzählen</h2>
              <p style={{ margin: 0 }}>Erzähle eine Geschichte zu allen ausgewählten Bildern!</p>
              <div style={{ 
                margin: '15px auto', 
                width: '50px',
                height: '50px',
                backgroundColor: usedImages.length === filledImages.length ? '#28a745' : '#6c757d',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease'
              }}>
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: 'white'
                }}>
                  {usedImages.length}/{filledImages.length}
                </span>
              </div>
            </div>

            <div style={{
              marginBottom: '20px',
              marginTop: '20px',
              display: 'flex',
              gap: (isPhone || isTabletPortraitMode) ? '0' : '20px',
              alignItems: 'flex-start',
              justifyContent: 'center',
              marginLeft: (isPhone || isTabletPortraitMode) ? '0' : '60px',
              flexDirection: (isPhone || isTabletPortraitMode) ? 'column' : 'row'
            }}>
            
            {/* Pictures section */}
            <div style={{ 
              flex: 'none', 
              backgroundColor: '#E8F5E8', 
              border: '2px solid #dee2e6', 
              borderRadius: '15px', 
              padding: '40px 95px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}>
              {/* Seated character in upper left */}
              <div style={{
                position: 'absolute',
                top: '-130px',
                left: '-35px',
                zIndex: 3
              }}>
                <img 
                  src={sprechheldenHeldSeated} 
                  alt="Sprechhelden Held Seated" 
                  style={{
                    width: '170px',
                    height: 'auto',
                    opacity: 0.9
                  }}
                />
              </div>
              
              {/* Help speech bubble - positioned next to seated character */}
              {showHelpText && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-170px',
                    left: '100px',
                    zIndex: 4,
                    backgroundColor: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '20px',
                    border: '2px solid #333333',
                    fontSize: '18px',
                    color: '#333',
                    textAlign: 'center',
                    minWidth: '280px',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  <span>
                    Ich sehe auf dem Bild...
                  </span>
                </div>
              )}
              {/* Top section - Geschichtenbilder (Story Images) */}
              <div style={{ marginBottom: isPhone ? '15px' : '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: isPhone ? '15px' : '20px', 
                  flexWrap: 'wrap',
                  flexDirection: isPhone ? 'column' : 'row',
                  alignItems: isPhone ? 'center' : 'flex-start'
                }}>
                  {filledImages
                    .filter(img => img.source === 'story_images')
                    .map((img, idx) => (
                      <div key={`story-${idx}`} style={{ textAlign: 'center', display: 'inline-block' }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`story-tooltip-${idx}`}>Klicke auf das Bild, um es als "verwendet" zu markieren</Tooltip>}
                        >
                        <div style={{
                          padding: '2px',
                          borderRadius: '8px',
                          backgroundColor: usedImages.includes(img.id) ? '#f0f8f0' : '#ffffff',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          border: `2px solid ${usedImages.includes(img.id) ? '#28a745' : '#dee2e6'}`,
                          display: 'inline-block',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => toggleImageUsed(img.id)}
                        >
                          {/* Checkmark overlay */}
                          {usedImages.includes(img.id) && (
                            <div style={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              width: '25px',
                              height: '25px',
                              backgroundColor: '#28a745',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                            }}>
                              <Icon name="check" size="sm" style={{ color: 'white' }} />
                            </div>
                          )}
                          
                          <img 
                            src={img.imageUrl} 
                            alt={img.name}
                            style={{ 
                              width: isPhone ? '60vw' : '218px',
                              height: isPhone ? '38vh' : '327px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              opacity: usedImages.includes(img.id) ? 0.8 : 1,
                              display: 'block'
                            }}
                          />
                        </div>
                        </OverlayTrigger>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Bottom section - Wortbilder (Word Images) */}
              <div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: isPhone ? '15px' : '20px', 
                  flexWrap: 'wrap',
                  flexDirection: isPhone ? 'column' : 'row',
                  alignItems: isPhone ? 'center' : 'flex-start'
                }}>
                  {filledImages
                    .filter(img => img.source === 'words')
                    .map((img, idx) => (
                      <div key={`word-${idx}`} style={{ width: '220px', textAlign: 'center' }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`word-tooltip-${idx}`}>Klicke auf die Karte, um sie als "verwendet" zu markieren</Tooltip>}
                        >
                        <div style={{
                          padding: '21px',
                          borderRadius: '12px',
                          backgroundColor: usedImages.includes(img.id) ? '#f0f8f0' : '#ffffff',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          border: `2px solid ${usedImages.includes(img.id) ? '#28a745' : '#dee2e6'}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => toggleImageUsed(img.id)}
                        >
                          {/* Checkmark overlay */}
                          {usedImages.includes(img.id) && (
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              width: '42px',
                              height: '42px',
                              backgroundColor: '#28a745',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                            }}>
                              <Icon name="check" size="md" style={{ color: 'white', fontSize: '18px' }} />
                            </div>
                          )}
                          
                          <img 
                            src={img.imageUrl} 
                            alt={img.name}
                            style={{ 
                              width: '169px', 
                              height: '169px', 
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '13px',
                              opacity: usedImages.includes(img.id) ? 0.8 : 1
                            }}
                          />
                          <h6 style={{ 
                            margin: '0', 
                            fontSize: '21px', 
                            fontWeight: 'bold',
                            color: usedImages.includes(img.id) ? '#28a745' : '#333'
                          }}>{img.name}</h6>
                        </div>
                        </OverlayTrigger>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            {/* Buttons section - positioned next to pictures (hidden on mobile and tablet portrait) */}
            {!isPhone && !isTabletPortraitMode && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                paddingTop: '20px',
                justifyContent: 'flex-start',
                alignItems: 'center'
              }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="finish-tooltip">
                {usedImages.length === filledImages.length 
                  ? 'Geschichte abschließen' 
                  : `Markiere alle Bilder (${usedImages.length}/${filledImages.length})`
                }
              </Tooltip>}
            >
              <Button 
                variant={usedImages.length === filledImages.length ? "success" : "outline-secondary"}
                size="lg"
                onClick={finishStory}
                disabled={usedImages.length !== filledImages.length}
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  opacity: usedImages.length === filledImages.length ? 1 : 0.5,
                  cursor: usedImages.length === filledImages.length ? 'pointer' : 'not-allowed'
                }}
              >
                <Icon name="check" size="lg" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="new-images-tooltip">Neue Bilder auswählen</Tooltip>}
            >
              <Button 
                variant="primary"
                size="lg"
                onClick={selectNewImages}
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="story" size="lg" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
            >
              <Button 
                variant="secondary"
                size="lg"
                onClick={selectNewImages}
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <LuGamepad2 size={24} />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="story-help-tooltip">Hilfe - Tipps für das Geschichtenerzählen</Tooltip>}
            >
              <Button
                onClick={handleHelpClick}
                variant="info"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  border: '2px solid #17a2b8',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img 
                  src={helpLogo} 
                  alt="Hilfe" 
                  style={{ 
                    width: '40px', 
                    height: '40px',
                    objectFit: 'contain'
                  }} 
                />
              </Button>
            </OverlayTrigger>
              </div>
            )}

          </div>

          {/* Control buttons below pictures for mobile mode and tablet portrait */}
          {(isPhone || isTabletPortraitMode) && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px',
              marginTop: '30px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '15px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="finish-tooltip-mobile">
                  {usedImages.length === filledImages.length
                    ? 'Geschichte abschließen'
                    : `Markiere alle Bilder (${usedImages.length}/${filledImages.length})`
                  }
                </Tooltip>}
              >
                <Button
                  variant={usedImages.length === filledImages.length ? "success" : "outline-secondary"}
                  size="lg"
                  onClick={finishStory}
                  disabled={usedImages.length !== filledImages.length}
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                    opacity: usedImages.length === filledImages.length ? 1 : 0.5,
                    cursor: usedImages.length === filledImages.length ? 'pointer' : 'not-allowed'
                  }}
                >
                  <Icon name="check" size="lg" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="new-images-tooltip-mobile">Neue Bilder auswählen</Tooltip>}
              >
                <Button
                  variant="primary"
                  size="lg"
                  onClick={selectNewImages}
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <Icon name="story" size="lg" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="back-to-games-tooltip-mobile">Zurück zu Spielen</Tooltip>}
              >
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={selectNewImages}
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <LuGamepad2 size={24} />
                </Button>
              </OverlayTrigger>
            </div>
          )}
          </>
        )}
      </Container>
      </div>
    );
  }

  // Laut Selection Modal
  const renderLautSelectionModal = () => {
    // Debug logging when modal is opened
    if (showLautModal) {
    }
    
    return (
      <Modal show={showLautModal} onHide={onBackToGames || (() => setShowLautModal(false))} backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header closeButton={true}>
        <Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h4 style={{ margin: 0 }}>Laute auswählen</h4>
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="laut-info-tooltip">Wähle bis zu 2 Laute aus, um Wörter mit diesen Lauten für das Geschichtenspiel zu verwenden.</Tooltip>}
            >
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#17a2b8', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                <Icon name="info" size="sm" />
              </div>
            </OverlayTrigger>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Wähle bis zu 2 Laute aus ({selectedLauts.length}/2 ausgewählt):
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
          gap: '15px',
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '10px'
        }}>
          {availableLauts.map(laut => {
            const isSelected = selectedLauts.includes(laut);
            const isDisabled = !isSelected && selectedLauts.length >= 2;
            
            return (
              <button
                key={laut}
                onClick={() => handleLautSelection(laut)}
                disabled={isDisabled}
                style={{
                  padding: '15px 10px',
                  border: isSelected ? '3px solid #28a745' : '2px solid #dee2e6',
                  borderRadius: '10px',
                  backgroundColor: isSelected ? '#d4edda' : isDisabled ? '#f8f9fa' : '#ffffff',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: isSelected ? '#155724' : isDisabled ? '#6c757d' : '#333',
                  transition: 'all 0.3s ease',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isDisabled ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {laut}
              </button>
            );
          })}
        </div>
        {selectedLauts.length >= 2 && (
          <p style={{ marginTop: '15px', color: '#dc3545', fontSize: '14px' }}>
            Maximale Anzahl von Lauten erreicht. Entferne einen Laut, um einen anderen auszuwählen.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={resetLautSelection}
          disabled={selectedLauts.length === 0}
        >
          Zurücksetzen
        </Button>
        <Button 
          variant="success" 
          onClick={startGameWithLauts}
          disabled={selectedLauts.length === 0}
          style={{
            minWidth: '150px',
            backgroundColor: '#28a745',
            borderColor: '#28a745'
          }}
        >
          Spiel starten ({selectedLauts.length} Laut{selectedLauts.length !== 1 ? 'e' : ''})
        </Button>
      </Modal.Footer>
    </Modal>
    );
  };

  // Alert Modal
  const renderAlertModal = () => {
    return (
      <Modal 
        show={showAlertModal} 
        onHide={() => setShowAlertModal(false)} 
        centered
        dialogClassName="custom-alert-modal"
      >
        <Modal.Body className="text-center" style={{ 
          padding: '30px',
          borderRadius: '15px'
        }}>
          <p style={{ fontSize: '18px', marginBottom: '25px', color: '#333' }}>{alertMessage}</p>
          <Button 
            variant="success" 
            onClick={() => setShowAlertModal(false)}
            style={{
              minWidth: '120px',
              height: '45px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            OK
          </Button>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: isPhone ? '30px 15px' : '20px',
      overflow: 'visible'
    }}>
      {!isPhone && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: '100vh',
          backgroundImage: `url(${background1})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: 0.2,
          zIndex: 0
        }} />
      )}
    <Container fluid style={{ position: 'relative', minHeight: '100vh', zIndex: 1 }}>
      {renderLautSelectionModal()}
      {renderAlertModal()}


      <div style={{
        backgroundColor: 'white',
        padding: isPhone ? '20px' : '40px',
        borderRadius: '15px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        margin: isPhone ? '20px auto' : '30px auto',
        maxWidth: isPhone ? '95%' : '90%'
      }}>
        <div className="text-center" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
            <h2 style={{ margin: 0, color: 'inherit', fontSize: isPhone ? '1.5rem' : '2rem' }}>
              Geschichte vorbereiten
            </h2>
          </div>
          <p style={{ margin: 0, color: '#666', fontSize: isPhone ? '14px' : '16px' }}>
            {difficulty === 'easy' ? 'Wähle 2 Wortbilder und 1 Geschichtenbild aus, zu denen das Kind eine Geschichte erzählen soll.' :
             difficulty === 'medium' ? 'Wähle 2 Wortbilder und 2 Geschichtenbilder aus, zu denen das Kind eine Geschichte erzählen soll.' :
             'Wähle 5 Bilder aus, zu denen das Kind eine Geschichte erzählen soll.'}
          </p>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto 40px' }}>
          <Form.Control
            type="text"
            placeholder="Bilder suchen (z.B. Tiere, Haus, Ball...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="lg"
            style={{
              borderRadius: '10px',
              border: '2px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          />
        </div>

        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {selectedImages.map((image, idx) => (
              <div
                key={idx}
                style={{
                  width: '150px',
                  height: '170px',
                  border: '3px dashed #dee2e6',
                  borderRadius: '15px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: image ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  padding: '15px'
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                onMouseEnter={() => setHoveredSlot(idx)}
                onMouseLeave={() => setHoveredSlot(null)}
              >
                {image ? (
                  <>
                    <img 
                      src={image.imageUrl} 
                      alt={image.name}
                      style={{ 
                        maxWidth: '120px', 
                        maxHeight: '120px', 
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                    />
                    <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold' }}>{image.name}</p>
                    {hoveredSlot === idx && (
                      <div
                        onClick={() => removeFromSlot(idx)}
                        style={{
                          position: 'absolute',
                          top: '0',
                          left: '0',
                          right: '0',
                          bottom: '0',
                          backgroundColor: 'rgba(220, 53, 69, 0.9)',
                          borderRadius: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <span style={{ 
                          color: 'white', 
                          fontSize: '48px', 
                          fontWeight: 'bold',
                          userSelect: 'none'
                        }}>
                          ×
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ color: '#6c757d', fontSize: '16px' }}>
                      {(difficulty === 'easy' || difficulty === 'medium') ? getSlotTypes()[idx].label : `Platz ${idx + 1}`}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
          {onBackToGames && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="back-to-games-prep-tooltip">Zurück zu Spielen</Tooltip>}
            >
              <Button 
                variant="secondary"
                onClick={onBackToGames}
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LuGamepad2 size={24} />
              </Button>
            </OverlayTrigger>
          )}
          {selectedLauts.length > 0 && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="lauts-tooltip">Ausgewählte Laute</Tooltip>}
            >
              <div
                style={{
                  minWidth: '80px',
                  height: '60px',
                  borderRadius: '30px',
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'default',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  padding: '0 15px'
                }}
              >
                {selectedLauts.join(', ')}
              </div>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="start-tooltip">Geschichte mit den ausgewählten Bildern beginnen</Tooltip>}
          >
            <Button 
              variant="success"
              size="lg"
              onClick={startStory}
              disabled={(() => {
                const filledSlots = selectedImages.filter(img => img !== null);
                const availableStoryCount = imageAssets.filter(img => img.source === 'story_images').length;
                if (difficulty === 'easy') {
                  if (filledSlots.length < 3) return true;

                  // Check for duplicates
                  const uniqueImages = new Set(filledSlots.map(img => img.id));
                  if (uniqueImages.size !== filledSlots.length) return true;

                  const wordImages = filledSlots.filter(img => img.source === 'words');
                  const storyImagesSelected = filledSlots.filter(img => img.source === 'story_images');
                  const requiredStory = Math.min(1, availableStoryCount);
                  return wordImages.length < (3 - requiredStory) || storyImagesSelected.length < requiredStory;
                } else if (difficulty === 'medium') {
                  if (filledSlots.length < 4) return true;

                  // Check for duplicates
                  const uniqueImages = new Set(filledSlots.map(img => img.id));
                  if (uniqueImages.size !== filledSlots.length) return true;

                  const wordImages = filledSlots.filter(img => img.source === 'words');
                  const storyImagesSelected = filledSlots.filter(img => img.source === 'story_images');
                  const requiredStory = Math.min(2, availableStoryCount);
                  return wordImages.length < (4 - requiredStory) || storyImagesSelected.length < requiredStory;
                }

                // For hard mode - check duplicates and minimum required
                const minRequired = 5;
                if (filledSlots.length < minRequired) return true;

                // Check for duplicates in hard mode
                const uniqueImages = new Set(filledSlots.map(img => img.id));
                return uniqueImages.size !== filledSlots.length;
              })()}
              style={{
                minWidth: '200px',
                minHeight: '60px',
                borderRadius: '30px',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              Geschichte starten
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="laut-change-tooltip">Laute ändern</Tooltip>}
          >
            <Button
              variant="outline-secondary"
              size="lg"
              onClick={() => setShowLautModal(true)}
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <Icon name="settings" size="lg" />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="random-tooltip">Zufällige Bilder auswählen</Tooltip>}
          >
            <Button
              onClick={selectRandomImages}
              disabled={imageAssets.length === 0 || selectedImages.filter(img => img === null).length === 0}
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#8B5CF6',
                borderColor: '#8B5CF6',
                color: 'white',
                border: '2px solid #8B5CF6',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = '#7C3AED';
                  e.target.style.borderColor = '#7C3AED';
                  e.target.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = '#8B5CF6';
                  e.target.style.borderColor = '#8B5CF6';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            >
              <Icon name="sparkle" size="lg" />
            </Button>
          </OverlayTrigger>
        </div>

      </div>

      {/* Story Images Collection (1x2 aspect ratio) */}
      {storyImagesFiltered.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: isPhone ? '20px' : '30px',
          borderRadius: '15px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          margin: isPhone ? '20px auto' : '30px auto',
          maxWidth: isPhone ? '95%' : '90%',
          marginBottom: '40px'
        }}>
          <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>Geschichtenbilder ({storyImagesFiltered.length} / {storyImages.length} gesamt)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px', maxWidth: '1200px', margin: '0 auto' }}>
            {storyImagesFiltered.map(image => (
              <div
                key={image.id}
                style={{
                  border: '2px solid #dee2e6',
                  borderRadius: '12px',
                  padding: '15px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ':hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, image)}
                onClick={() => handleImageClick(image)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ position: 'relative', width: '120px', height: '160px', marginBottom: '8px', overflow: 'hidden', borderRadius: '8px' }}>
                  <div
                    className="story-shimmer"
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '100%', height: '100%',
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
                      backgroundSize: '200% 100%',
                      animation: 'storyShimmer 1.4s ease-in-out infinite'
                    }}
                  />
                  <img
                    src={image.imageUrl}
                    alt={image.name}
                    loading="lazy"
                    style={{
                      maxWidth: '120px',
                      maxHeight: '160px',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      position: 'relative',
                      zIndex: 1,
                      opacity: 0,
                      transition: 'opacity 0.4s ease-in'
                    }}
                    onLoad={(e) => {
                      e.target.style.opacity = '1';
                      const shimmer = e.target.parentElement.querySelector('.story-shimmer');
                      if (shimmer) shimmer.style.display = 'none';
                    }}
                  />
                </div>
                <p style={{ margin: '0', fontSize: '12px', fontWeight: 'bold' }}>{image.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Words Collection Images */}
      {wordsImages.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: isPhone ? '20px' : '30px',
          borderRadius: '15px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          margin: isPhone ? '20px auto' : '30px auto',
          maxWidth: isPhone ? '95%' : '90%',
          marginBottom: '40px'
        }}>
          <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>Wortbilder</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px', maxWidth: '1200px', margin: '0 auto' }}>
            {wordsImages.map(image => (
              <div
                key={image.id}
                style={{
                  border: '2px solid #dee2e6',
                  borderRadius: '12px',
                  padding: '15px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  ':hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, image)}
                onClick={() => handleImageClick(image)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ position: 'relative', marginBottom: '8px', overflow: 'hidden', borderRadius: '8px', minWidth: '80px', minHeight: '80px' }}>
                  <div
                    className="story-shimmer"
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '100%', height: '100%',
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
                      backgroundSize: '200% 100%',
                      animation: 'storyShimmer 1.4s ease-in-out infinite'
                    }}
                  />
                  <img
                    src={image.imageUrl}
                    alt={image.name}
                    loading="lazy"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      position: 'relative',
                      zIndex: 1,
                      opacity: 0,
                      transition: 'opacity 0.4s ease-in'
                    }}
                    onLoad={(e) => {
                      e.target.style.opacity = '1';
                      const shimmer = e.target.parentElement.querySelector('.story-shimmer');
                      if (shimmer) shimmer.style.display = 'none';
                    }}
                  />
                </div>
                <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold' }}>{image.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredImages.length === 0 && !loading && (
        <div style={{
          backgroundColor: 'white',
          padding: isPhone ? '30px 20px' : '40px',
          borderRadius: '15px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          margin: isPhone ? '20px auto' : '30px auto',
          maxWidth: isPhone ? '95%' : '90%',
          textAlign: 'center'
        }}>
          <p style={{ color: '#6c757d', fontSize: '18px', margin: '0 0 20px 0' }}>
            {searchTerm 
              ? 'Keine Bilder gefunden. Versuche eine andere Suche.'
              : selectedLauts.length > 0 
                ? `Keine Bilder für die ausgewählten Laute "${selectedLauts.join(', ')}" gefunden. Wähle andere Laute aus.`
                : 'Keine Bilder verfügbar. Bitte wähle Laute aus.'
            }
          </p>
          {selectedLauts.length > 0 && !searchTerm && (
            <Button 
              variant="info" 
              onClick={() => setShowLautModal(true)}
              style={{
                minWidth: '150px',
                minHeight: '50px',
                borderRadius: '25px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Laute ändern
            </Button>
          )}
        </div>
      )}
    </Container>
    </div>
  );
}

export default StoryGame;